import { Bell, CheckCircle2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { localeKey, useNotifications, useUnreadCount } from '@/features/notifications';
import { ROUTES } from '@/shared/config/routes';
import { useClickOutside, useEscapeKey } from '@/shared/hooks';
import { cn } from '@/shared/lib';

/**
 * Bell with an unread dot and a popover list. Master dashboard only.
 *
 * It used to carry its own two-item mock with hard-coded text keys — a second
 * invention alongside the feed's own. Both read the same endpoint now, so the
 * dot means something.
 */
export const NotificationsMenu = () => {
  const { t } = useTranslation(['app', 'notifications']);
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const { unread, latest } = useUnreadCount();
  const { markAllRead } = useNotifications();

  useClickOutside(containerRef, () => setOpen(false), open);
  useEscapeKey(() => setOpen(false), open);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={t('app:header.notifications')}
        className="relative rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 animate-pulse rounded-full bg-secondary ring-2 ring-white" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 z-50 mt-2 w-80 origin-top-right overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-gray-100 p-4">
              <h3 className="font-bold text-gray-900">{t('app:header.notifications')}</h3>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-700"
                >
                  {t('app:header.markAllRead')}
                </button>
              )}
            </div>

            <ul className="max-h-[300px] overflow-y-auto">
              {latest.length === 0 ? (
                <li className="p-4 text-sm font-medium text-gray-500">
                  {t('notifications:empty.body')}
                </li>
              ) : (
                latest.map((item) => (
                  <li
                    key={item.id}
                    className={cn(
                      'flex gap-3 border-b border-gray-50 p-4 transition-colors hover:bg-gray-50',
                      !item.read && 'bg-indigo-50/30',
                    )}
                  >
                    <div className="mt-1">
                      <CheckCircle2
                        size={16}
                        className={item.read ? 'text-gray-300' : 'text-primary'}
                      />
                    </div>
                    <div>
                      <div
                        className={cn(
                          'text-sm',
                          item.read ? 'font-medium text-gray-700' : 'font-bold text-gray-900',
                        )}
                      >
                        {t(`notifications:texts.${localeKey(item.type)}`, item.params)}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {t(`notifications:times.${item.time.key}`, item.time.params)}
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>

            <div className="border-t border-gray-100 bg-gray-50 p-3 text-center">
              <Link
                to={ROUTES.app.notifications}
                onClick={() => setOpen(false)}
                className="inline-block w-full text-sm font-bold text-primary transition-colors hover:text-primary-hover"
              >
                {t('app:header.viewAll')}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
